import { LIBRARY_IDS } from '../../constants/api';
import { PRODUCT_FILTERS, PRODUCT_FILTER_KEYS } from '../../constants/ui';
import { ENV } from '../../constants/environments';

import { FiltersProps } from '../../types/components/products/Filters';
import { ProductFilterOptionConfig } from '../../types/ui/productFilters';

import {
  DownChevronIcon,
  SmallExitIcon,
  SearchIcon,
} from '../../modules/Icons';
import { Menu } from '@headlessui/react';
import { DynamicFilterButtons } from '../../components/shared/DynamicFilterButtons';
import TrendingCategories from './TrendingCategories';

const {
  TEXT_SEARCH_V2: TEXT_SEARCH_KEY_V2,
  TEXT_SEARCH_V1: TEXT_SEARCH_KEY_V1,
} = PRODUCT_FILTER_KEYS;
const {
  LIBRARY_ID_2D,
  LIBRARY_ID_3D,
  LIBRARY_ID_PFL,
} = LIBRARY_IDS;

import { useState, useCallback, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import debounce from 'lodash.debounce';

const Filters: React.FC<FiltersProps> = ({
  libraryId,
  category,
  allCategories,
  trendingCategoriesResults,
  hideSortBy,
  onFiltersApplied,
}) => {
  const router = useRouter();
  const routerQueryRef = useRef(router.query);
  const [selectedOption, setSelectedOption] = useState<ProductFilterOptionConfig>();

  useEffect(() => {
    routerQueryRef.current = router.query;
  }, [router.query]);

  useEffect(() => {
    if (!libraryId || !PRODUCT_FILTERS[libraryId]) return;
    const options = PRODUCT_FILTERS[libraryId].options;
    const defaultOption = options.find(o => !o.queryParamValue);
    setSelectedOption(defaultOption);
  }, [libraryId]);

  const initializeSelectedFilter = () => {
    if (!PRODUCT_FILTERS[libraryId]) return;
    const options = PRODUCT_FILTERS[libraryId].options;
    const filterKey = options[0].queryParamKey;
    if (!router.query[filterKey]) return;
    const selectedOption = options.find(o => o.queryParamValue === router.query[filterKey]);
    setSelectedOption(selectedOption);
  };

  useEffect(() => {
    initializeSelectedFilter();
  }, [routerQueryRef.current, libraryId]);

  const getDefaultRoute = (): string => {
    switch (libraryId) {
      case LIBRARY_ID_2D: return '/products';
      case LIBRARY_ID_3D: return '/3d';
      case LIBRARY_ID_PFL: return '/practice-footage';
      default: return '';
    };
  };

  const getSearchInputPlaceholder = (): string => {
    if (shouldRenderSearchTermUi()) return '';
    switch (libraryId) {
      case LIBRARY_ID_2D: return 'Search 11,000+ assets';
      case LIBRARY_ID_3D: return 'Search all 3D assets';
      case LIBRARY_ID_PFL: return 'Search 500+ clips';
      default: return '';
    };
  };

  const getTextSearchKey = (): string => {
    return ENV.useV2ProductEndpoints ? TEXT_SEARCH_KEY_V2 : TEXT_SEARCH_KEY_V1;
  }

  const getTextSearchTerm = (): string => {
    return routerQueryRef.current[getTextSearchKey()] as string;
  }

  const executeSearch = (cleanedUpQuery: { [key: string]: string | string[] }) => {
    if (onFiltersApplied) {
      onFiltersApplied(cleanedUpQuery);
      return;
    }
    router.push({ href: router.route, query: cleanedUpQuery });
  }

  const getCleanedUpQuery = (): { [key: string]: string | string[] } => {
    // remove the query id query param, as the new search will yield new query id too
    // use a ref b/c the text search is debounced and might get wrong router query state otherwise
    const { query_id, ...cleanedUpQuery } = routerQueryRef.current;
    return cleanedUpQuery;
  }

  const debouncedSearch = useCallback(
    debounce((nextValue: string) => {
      const searchTerm = nextValue;
      const textSearchKey = getTextSearchKey();
      const cleanedUpQuery = getCleanedUpQuery();
      searchTerm?.length
        ? cleanedUpQuery[textSearchKey] = searchTerm
        : delete cleanedUpQuery[textSearchKey];
      executeSearch(cleanedUpQuery);
    }, 500),
    []
  );

  const handleFiltersChange = (option: ProductFilterOptionConfig) => {
    setSelectedOption(option);
    const cleanedUpQuery = getCleanedUpQuery();
    option?.queryParamValue
      ? cleanedUpQuery[option.queryParamKey] = option.queryParamValue
      : delete cleanedUpQuery[option.queryParamKey];
    executeSearch(cleanedUpQuery);
  };

  const handleTextSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    debouncedSearch(nextValue);
  };

  const clearTextSearch = () => {
    !!category ? router.push(getDefaultRoute()) : debouncedSearch('');
  };

  const shouldRenderSearchTermUi = (): boolean => !!category;

  const shouldRenderPopularCategoriesUi = (): boolean => !router.query[getTextSearchKey()] && !!allCategories && !!trendingCategoriesResults && libraryId === LIBRARY_ID_2D;

  const renderSearchTerm = (): React.ReactElement => {
    if (!shouldRenderSearchTermUi()) return;
    return (
      <span
        className="ml-3 mr-1 flex items-center text-[14px] text-white bg-a-blue hover:bg-blue-1000 px-2 py-1 whitespace-nowrap rounded-[5px] cursor-pointer"
        onClick={() => clearTextSearch()}
      >
        {category?.name}
        <SmallExitIcon className="inline h-4 ml-1 cursor-pointer stroke-white fill-white" />
      </span>
    );
  };

  const render2DFiltersBlock = (): React.ReactElement => {
    if (libraryId !== LIBRARY_ID_2D || !PRODUCT_FILTERS[libraryId] || !!hideSortBy) return;
    const filters = PRODUCT_FILTERS[libraryId];
    return (
      <div className="md:min-w-[220px] md:mt-4 lg:mt-0 text-right p-2 md:p-0" cy-test-id="2d-filters-block">
        <label className="text-sm text-a-gray">Sort by</label>
        <Menu>
          <Menu.Button>
            <span className="relative ml-3">
              {selectedOption?.name || filters.options[0].name}{' '}
              <DownChevronIcon className="inline ml-1.5 mb-1 scale-[1.2] stroke-white" />
            </span>
          </Menu.Button>
          <Menu.Items className="absolute bg-gray-1000 z-30 right-6 mt-4 rounded-[5px] shadow-md overflow-hidden">
            {filters.options.map((option, index) => (
              <Menu.Item key={index}>
                <div
                  key={option.queryParamKey}
                  className="text-left px-4 py-2 hover:bg-gray-800 cursor-pointer text-[14px]"
                  onClick={() => handleFiltersChange(option)}
                >
                  {option.name}
                </div>
              </Menu.Item>
            ))}
          </Menu.Items>
        </Menu>
      </div>
    );
  };

  const render3DFiltersBlock = (): React.ReactElement => {
    if (libraryId !== LIBRARY_ID_3D || !PRODUCT_FILTERS[libraryId] || hideSortBy) return;
    const filters = PRODUCT_FILTERS[libraryId];
    return (
      <div className="pt-6">
        <DynamicFilterButtons
          activeFilter={selectedOption?.queryParamValue}
          buttonsConfig={filters.options.map(o => ({
            label: o.name,
            category: o.queryParamValue,
            IconComponent: o.icon,
            total: null,
          }))}
          setFilter={(value: string) => {
            const option = filters.options.find(o => o.queryParamValue === value);
            handleFiltersChange(option);
          }}
        />
      </div>
    );
  };

  const renderPopularCategories = (): React.ReactElement => {
    if (!shouldRenderPopularCategoriesUi()) return;
    return <TrendingCategories allCategories={allCategories} trendingCategoriesResults={trendingCategoriesResults}/>;
  };

  return (
    <div
      cy-test-id="product-filters"
    >
      <div className="pb-8">
        <div className="items-center w-full gap-8 lg:flex">
          <div className="relative w-full">
            <div className="rounded-[5px] flex items-center px-4 py-3 bg-a-dark-gray md:min-w-[400px]">
              <SearchIcon className="inline w-4 stroke-white" />
              {renderSearchTerm()}
              <input
                className="w-full pl-2 text-sm bg-transparent outline-0"
                placeholder={getSearchInputPlaceholder()}
                onChange={handleTextSearch}
                defaultValue={getTextSearchTerm()}
              />
            </div>
          </div>
          {render2DFiltersBlock()}
        </div>
        {renderPopularCategories()}
        {render3DFiltersBlock()}
      </div>
    </div>
  );
};

export default Filters;
